import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Layout from '../../components/Layout/Layout';
import {
  Card, Button, Badge, SearchInput, Spinner, EmptyState,
  Table, TableHead, TableBody, TableRow, TableCell, Modal, Tooltip
} from '../../components/UI';
import { scriptsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { HiOutlinePlus, HiOutlineDocumentText, HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi';
import ScriptModal from './ScriptModal';

// Build compact pagination with ellipsis
const buildPageList = (current, total) => {
  const pages = [];
  const set = new Set([1, total, current, current - 1, current + 1]);
  for (const p of set) {
    if (p >= 1 && p <= total) pages.push(p);
  }
  pages.sort((a, b) => a - b);

  const result = [];
  for (let i = 0; i < pages.length; i++) {
    result.push(pages[i]);
    if (i < pages.length - 1 && pages[i + 1] - pages[i] > 1) {
      result.push('...');
    }
  }
  return result;
};

const ScriptsList = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [showModal, setShowModal] = useState(false);
  const [selectedScript, setSelectedScript] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [scriptToDelete, setScriptToDelete] = useState(null);

  // Fetch scripts
  const { data: scriptsData, isLoading } = useQuery({
    queryKey: ['scripts'],
    queryFn: async () => {
      const response = await scriptsAPI.getAll();
      return response.data?.data || [];
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => scriptsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['scripts']);
      toast.success('Script deleted successfully');
      setShowDeleteModal(false);
      setScriptToDelete(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete script');
    },
  });

  const scripts = scriptsData || [];

  const filteredScripts = search
    ? scripts.filter(s =>
        s.title?.toLowerCase().includes(search.toLowerCase()) ||
        (s.productIds || []).some(p =>
          (typeof p === 'object' ? p.name : '').toLowerCase().includes(search.toLowerCase())
        )
      )
    : scripts;

  // Client-side pagination
  const totalPages = Math.max(1, Math.ceil(filteredScripts.length / pageSize));
  const safePage = Math.min(page, totalPages);
  if (safePage !== page) {
    setPage(safePage);
  }
  const paginatedScripts = filteredScripts.slice((safePage - 1) * pageSize, safePage * pageSize);

  const handleAdd = () => {
    setSelectedScript(null);
    setShowModal(true);
  };

  const handleEdit = (script) => {
    setSelectedScript(script);
    setShowModal(true);
  };

  const handleDeleteClick = (script) => {
    setScriptToDelete(script);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (scriptToDelete) {
      deleteMutation.mutate(scriptToDelete._id);
    }
  };

  const renderProductBadges = (script) => {
    const products = script.productIds || [];
    if (products.length === 0) {
      return <span className="text-gray-400 text-sm">No product</span>;
    }
    return (
      <div className="flex flex-wrap gap-1">
        {products.slice(0, 3).map((p) => (
          <Badge key={p._id} variant="primary" size="sm">
            {p.name}
          </Badge>
        ))}
        {products.length > 3 && (
          <Badge variant="default" size="sm">+{products.length - 3}</Badge>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Scripts</h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 sm:mt-1">
              Create call scripts for your products
            </p>
          </div>
          <Button
            variant="primary"
            size="sm"
            icon={<HiOutlinePlus className="w-4 h-4" />}
            onClick={handleAdd}
          >
            <span className="hidden sm:inline">Add Script</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>

        {/* Search */}
        <Card padding="p-3 sm:p-4">
          <SearchInput
            value={search}
            onChange={handleSearchChange}
            placeholder="Search scripts or products..."
          />
        </Card>

        {/* Scripts List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : filteredScripts.length === 0 ? (
          <Card>
            <EmptyState
              icon={<HiOutlineDocumentText className="w-8 h-8 text-gray-400" />}
              title="No scripts found"
              description="Create your first call script to guide telecallers"
            />
          </Card>
        ) : (
          <Card padding="p-0">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow hoverable={false}>
                    <TableCell header>Title</TableCell>
                    <TableCell header>Products</TableCell>
                    <TableCell header>Status</TableCell>
                    <TableCell header>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedScripts.map((script) => (
                    <TableRow key={script._id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <HiOutlineDocumentText className="w-5 h-5 text-primary-600" />
                          </div>
                          <span className="font-medium text-gray-900">{script.title}</span>
                        </div>
                      </TableCell>
                      <TableCell>{renderProductBadges(script)}</TableCell>
                      <TableCell>
                        <Badge variant={script.isActive !== false ? 'success' : 'danger'} size="sm">
                          {script.isActive !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Tooltip text="Edit Script">
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<HiOutlinePencil className="w-4 h-4" />}
                              onClick={(e) => { e.stopPropagation(); handleEdit(script); }}
                            />
                          </Tooltip>
                          <Tooltip text="Delete Script">
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                              onClick={(e) => { e.stopPropagation(); handleDeleteClick(script); }}
                            />
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {paginatedScripts.map((script) => (
                <div key={script._id} className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      <div className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <HiOutlineDocumentText className="w-5 h-5 text-primary-600" />
                      </div>
                      <p className="font-medium text-gray-900 truncate">{script.title}</p>
                    </div>
                    <Badge variant={script.isActive !== false ? 'success' : 'danger'} size="sm">
                      {script.isActive !== false ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="mb-3">{renderProductBadges(script)}</div>
                  <div className="flex items-center justify-end gap-1 pt-2 border-t border-gray-100">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2"
                      icon={<HiOutlinePencil className="w-4 h-4 text-gray-500" />}
                      onClick={() => handleEdit(script)}
                      title="Edit"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2"
                      icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                      onClick={() => handleDeleteClick(script)}
                      title="Delete"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
<div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 sm:p-4 border-t border-gray-100">
  <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 text-xs sm:text-sm text-gray-500">
    <span>
      Showing {(safePage - 1) * pageSize + 1}–
      {Math.min(safePage * pageSize, filteredScripts.length)} of{" "}
      {filteredScripts.length}
    </span>

    <div className="inline-flex items-center gap-2 whitespace-nowrap shrink-0">
      <label
      htmlFor="script-rows-per-page"
      className="text-xs sm:text-sm text-gray-500 whitespace-nowrap"
      >
      Rows per page:
      </label>

      <select
        id="script-rows-per-page"
        value={pageSize}
        onChange={(e) => {
          setPageSize(Number(e.target.value));
          setPage(1);
        }}
        className="w-20 px-2 py-1 border border-gray-200 rounded-md text-sm bg-white shrink-0"
      >
        <option value={10}>10</option>
        <option value={25}>25</option>
        <option value={50}>50</option>
        <option value={100}>100</option>
      </select>
    </div>
  </div>

    <div className="flex items-center gap-1.5 sm:gap-2">
      <Button
        variant="outline"
        size="sm"
        disabled={safePage === 1}
        onClick={() => setPage((p) => p - 1)}
      >
        <span className="hidden sm:inline">Previous</span>
        <span className="sm:hidden">Prev</span>
      </Button>

      {buildPageList(safePage, totalPages).map((item, idx) =>
        item === "..." ? (
          <span
            key={`ellipsis-${idx}`}
            className="px-2 text-sm text-gray-400 select-none"
          >
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => setPage(item)}
            className={
              safePage === item
                ? "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-semibold bg-primary-600 text-white transition-colors"
                : "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            }
          >
            {item}
          </button>
        )
      )}

      <Button
        variant="outline"
        size="sm"
        disabled={safePage === totalPages}
        onClick={() => setPage((p) => p + 1)}
      >
        Next
      </Button>
    </div>
</div>
          </Card>
        )}

        {/* Script Modal */}
        <ScriptModal
          isOpen={showModal}
          onClose={() => { setShowModal(false); setSelectedScript(null); }}
          script={selectedScript}
          onSuccess={() => queryClient.invalidateQueries(['scripts'])}
        />

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Delete Script"
          size="sm"
        >
          <div className="space-y-3 sm:space-y-4">
            <p className="text-gray-600 text-sm">
              Are you sure you want to delete{' '}
              <span className="font-semibold">{scriptToDelete?.title}</span>? This action cannot be undone.
            </p>
            <div className="flex gap-2 sm:gap-3 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleConfirmDelete}
                loading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </Layout>
  );
};

export default ScriptsList;